import type { CustomCellRendererProps } from "ag-grid-react";
import { clsx } from "clsx";
import { formatDateTimeFromISO } from "widgets";

import style from "../styles/Cell.module.css";
import { extractValueAsArray } from "../utils/tables.ts";
import { TruncateListText } from "./TruncateListText.tsx";

export const DateListCell = (props: CustomCellRendererProps) => {
  /**
   * For lists we keep a stable, compact representation (date-only) so that more items can fit in the cell.
   * Each item is formatted via `formatDateTimeFromISO(date, { hideTime: true })` (localized short date).
   *
   * Example (Node en-US, for "2011-12-11T11:32:00.000Z"):
   * - "Dec 11, 2011"
   *
   * The list rendering itself is handled by `TruncateListText` which will collapse to:
   * - "date1, date2, date3" (if all fit)
   * - "date1, date2… +N" / "date1… +N" (when space is constrained)
   *
   * If we ever need the same progressive formatting as `DateCell` (full → short → numeric → year),
   * we should implement a list-wide variant selector first (to avoid mixing formats within one list).
   */
  const multiValues = extractValueAsArray(props.value);

  const items = multiValues.map((date) => formatDateTimeFromISO(String(date ?? ""), { hideTime: true }));
  const listString = items.join(", ");

  return (
    <div className={clsx(style.cell, style.truncate_container)}>
      <span className={style.truncate_autosize} aria-hidden>{listString}</span>
      <TruncateListText items={items} className={style.truncate_content} />
    </div>
  );
};
